import React, { FC, PropsWithChildren, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface ModalProps {
  title: string;
  onClose?: () => void;
  /** Visually hide the heading (still announced). */
  hideTitle?: boolean;
  suit?: 'spades' | 'hearts' | 'diamonds' | 'clubs';
}

export const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  '[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Small accessible dialog: focus moves in, Tab is kept inside, Escape closes,
 * focus returns to the opener on close, and the page behind does not scroll.
 *
 * The focus setup runs once per open. Callers usually pass an inline
 * `onClose`, and the table re-renders on every socket message, so `onClose`
 * is read from a ref rather than being an effect dependency (otherwise focus
 * would jump back to the first button on each update).
 */
export const Modal: FC<PropsWithChildren<ModalProps>> = ({
  title,
  onClose,
  hideTitle,
  suit = 'spades',
  children,
}) => {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusables = () =>
      Array.from(node?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) || []);
    (focusables()[0] || node)?.focus();

    const { body } = document;
    const prevOverflow = body.style.overflow;
    body.style.overflow = 'hidden';

    const onKey = (e: KeyboardEvent) => {
      const close = onCloseRef.current;
      if (e.key === 'Escape' && close) {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (!active || !node?.contains(active)) {
        // focus escaped (e.g. clicked the backdrop): pull it back in
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === node)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      body.style.overflow = prevOverflow;
      if (opener && document.contains(opener)) opener.focus?.();
    };
  }, []);

  return createPortal(
    <div
      className="st-overlay"
      onClick={e => {
        if (e.target === e.currentTarget) onCloseRef.current?.();
      }}
    >
      <div
        ref={ref}
        className="st-card st-stack"
        data-suit={suit}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        tabIndex={-1}
      >
        <h2 id={`${id}-title`} className={hideTitle ? 'st-sr-only' : 'st-h2'}>
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body
  );
};
